import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Image, Prohibit, ShoppingCartSimple, TrendUp } from "@phosphor-icons/react";
import { containerProxyUrl, livePhotoUrl, formatCellValue, isContainerField } from "@/lib/fmUtils";
import { ProductHoverPreview } from "@/components/ProductHoverPreview";
import { ChinaQuoteLink } from "@/components/ChinaQuoteLink";

export default function ProductGrid({
  layout,
  columns,
  records,
  fieldTypeMap,
  loading,
  onRowClick,
  onAddToCart,
  photoVersion,
  showHoverPreview = true,
  mobileLayout = false,
  selectable = false,
  selectedIds,
  onToggleSelect,
  bestSellingLayout = false,
  showBestBadge = false,
  showVariantSummary = false,
}) {
  const imageColumn = columns.find((col) => isContainerField(fieldTypeMap[col.key], col.key));
  const textColumns = columns.filter((col) => !isContainerField(fieldTypeMap[col.key], col.key));
  const itemNoCol = columns.find((col) => col.label === "Item No");
  const descCol = columns.find((col) => col.label === "Description");
  const qtyCol = columns.find((col) => col.label === "Quantity");
  const cbmCol = columns.find((col) => col.label === "CBM");

  if (loading) {
    return (
      <div
        className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
        data-testid="product-grid-skeleton"
      >
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} className="aspect-[3/4] w-full rounded-sm" />
        ))}
      </div>
    );
  }

  if (!records || records.length === 0) {
    return (
      <div
        className="flex h-64 items-center justify-center text-sm text-zinc-500"
        data-testid="product-grid-empty-state"
      >
        No records found.
      </div>
    );
  }

  return (
    <div
      className={
        bestSellingLayout
          ? "grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          : "grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
      }
      data-testid="product-grid"
    >
      {records.map((rec, idx) => {
        const imageUrl = imageColumn ? rec.fieldData?.[imageColumn.key] : null;
        const src = imageUrl ? (layout ? livePhotoUrl(layout, rec.recordId, imageColumn.key, photoVersion) : containerProxyUrl(imageUrl)) : null;
        const isSelected = selectable && selectedIds?.has(rec.recordId);
        const selectionCheckbox = selectable && (
          <div
            className="absolute right-1.5 top-1.5 rounded-sm bg-white/90 p-1"
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect(rec);
            }}
            data-testid={`select-product-checkbox-${rec.recordId}`}
          >
            <Checkbox checked={!!isSelected} className="h-5 w-5 rounded-none border-zinc-400" />
          </div>
        );
        const bestBadge =
          showBestBadge && rec._best_selling ? (
            <span
              className="absolute left-1.5 top-1.5 z-10 flex items-center gap-0.5 rounded-sm bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-sm"
              data-testid={`best-selling-badge-${rec.recordId}`}
            >
              <TrendUp size={10} weight="bold" /> Best
            </span>
          ) : null;
        const blockedBadge = rec.block ? (
          <span
            className="absolute bottom-1.5 left-1.5 z-10 flex flex-col items-start gap-0 rounded-sm bg-red-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-sm"
            data-testid={`blocked-badge-${rec.recordId}`}
          >
            <span className="flex items-center gap-0.5">
              <Prohibit size={10} weight="bold" /> Blocked
            </span>
            <span className="font-medium normal-case tracking-normal">
              by {rec.block.blocked_by || "-"}
              {rec.block.blocked_at ? ` · ${new Date(rec.block.blocked_at).toLocaleDateString()}` : ""}
            </span>
          </span>
        ) : null;
        const imageBox = (
          <div
            className={`relative flex items-center justify-center bg-zinc-50 ${
              bestSellingLayout ? "h-32 w-32 shrink-0 border-r border-zinc-200" : "aspect-square border-b border-zinc-200"
            }`}
          >
            {bestBadge}{blockedBadge}{src ? (
              <img
                src={src}
                alt="thumbnail"
                loading="lazy"
                decoding="async"
                className="h-full w-full object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <Image size={28} className="text-zinc-300" />
            )}
            {onAddToCart && (
              <Button
                size="sm"
                variant="outline"
                className="absolute bottom-1.5 right-1.5 h-auto rounded-sm border-zinc-300 bg-white/90 p-1.5 shadow-sm hover:bg-blue-600 hover:text-white"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToCart(rec);
                }}
                title="Add to Quote"
                data-testid={`add-to-cart-grid-button-${rec.recordId}`}
              >
                <ShoppingCartSimple size={14} />
              </Button>
            )}
            {!bestSellingLayout && selectionCheckbox}
          </div>
        );

        if (bestSellingLayout) {
          return (
            <div
              key={rec.recordId}
              onClick={() => onRowClick(rec, idx)}
              className={`flex cursor-pointer flex-col overflow-hidden rounded-sm border bg-white transition-colors duration-150 hover:border-blue-600 ${
                isSelected ? "border-blue-600 ring-1 ring-blue-600" : "border-zinc-300"
              }`}
              data-testid={`product-grid-card-${rec.recordId}`}
            >
              <div className="relative flex border-b border-zinc-200">
                {showHoverPreview ? (
                  <ProductHoverPreview
                    imgSrc={src}
                    description={rec.fieldData?.description}
                    itemNo={rec.fieldData?.["item no"]}
                    variants={rec.portalData?.product}
                  >
                    {imageBox}
                  </ProductHoverPreview>
                ) : (
                  imageBox
                )}
                <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 p-2 pr-9">
                  <div className="text-sm font-semibold text-zinc-900" data-testid={`best-selling-item-no-${rec.recordId}`}>
                    <ChinaQuoteLink itemNo={rec.fieldData?.["item no"]} />
                  </div>
                  <div className="line-clamp-4 text-xs text-zinc-600" data-testid={`best-selling-description-${rec.recordId}`}>
                    {rec.fieldData?.description || "-"}
                  </div>
                </div>
                {selectionCheckbox}
              </div>
              <div className="flex min-h-[100px] items-center justify-center bg-white p-1">
                {rec.fieldData?.selling_history ? (
                  <img
                    src={livePhotoUrl(layout, rec.recordId, "selling_history", photoVersion)}
                    alt="selling history"
                    className="w-full object-contain"
                    loading="lazy"
                    data-testid={`selling-history-image-${rec.recordId}`}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <span className="text-[10px] uppercase tracking-[0.05em] text-zinc-300">No selling history</span>
                )}
              </div>
            </div>
          );
        }

        return (
          <div
            key={rec.recordId}
            onClick={() => onRowClick(rec, idx)}
            className="flex cursor-pointer flex-col overflow-hidden rounded-sm border border-zinc-300 bg-white transition-colors duration-150 hover:border-blue-600"
            data-testid={`product-grid-card-${rec.recordId}`}
          >
            {showHoverPreview ? (
              <ProductHoverPreview
                imgSrc={src}
                description={rec.fieldData?.description}
                itemNo={rec.fieldData?.["item no"]}
                variants={rec.portalData?.product}
              >
                {imageBox}
              </ProductHoverPreview>
            ) : (
              imageBox
            )}
            {mobileLayout ? (
              <div className="flex flex-col gap-1 p-2" data-testid={`product-grid-mobile-info-${rec.recordId}`}>
                <div
                  className="truncate text-sm font-semibold text-zinc-900"
                  data-testid={`product-grid-mobile-item-no-${rec.recordId}`}
                >
                  {itemNoCol ? <ChinaQuoteLink itemNo={rec.fieldData?.[itemNoCol.key]} /> : "-"}
                </div>
                <div
                  className="line-clamp-2 text-xs text-zinc-600"
                  data-testid={`product-grid-mobile-description-${rec.recordId}`}
                >
                  {descCol ? formatCellValue(fieldTypeMap[descCol.key], rec.fieldData?.[descCol.key]) : "-"}
                </div>
                <div
                  className="flex items-center justify-between text-xs font-mono-data text-zinc-700"
                  data-testid={`product-grid-mobile-qty-cbm-${rec.recordId}`}
                >
                  <span>
                    QTY{" "}
                    {qtyCol && rec.fieldData?.[qtyCol.key] !== "" && rec.fieldData?.[qtyCol.key] != null
                      ? Math.round(Number(rec.fieldData[qtyCol.key]) || 0)
                      : "-"}
                  </span>
                  <span>
                    {cbmCol ? formatCellValue(fieldTypeMap[cbmCol.key], rec.fieldData?.[cbmCol.key]) : "-"} CBM
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-0.5 p-2">
                {itemNoCol && (
                  <div
                    className="truncate text-sm font-bold text-zinc-900"
                    data-testid={`product-grid-item-no-${rec.recordId}`}
                  >
                    <ChinaQuoteLink itemNo={rec.fieldData?.[itemNoCol.key]} />
                  </div>
                )}
                {descCol && (
                  <div
                    className="line-clamp-2 text-xs text-zinc-600"
                    data-testid={`product-grid-description-${rec.recordId}`}
                  >
                    {formatCellValue(fieldTypeMap[descCol.key], rec.fieldData?.[descCol.key])}
                  </div>
                )}
                {textColumns
                  .filter((col) => col !== itemNoCol && col !== descCol)
                  .map((col) => {
                    const meta = fieldTypeMap[col.key];
                    const value = rec.fieldData?.[col.key];
                    return (
                      <div key={col.key} className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-[10px] uppercase tracking-[0.03em] text-zinc-400">
                          {col.label}
                        </span>
                        <span
                          className={`truncate text-xs text-zinc-800 ${
                            meta?.result === "number" ? "font-mono-data" : ""
                          }`}
                        >
                          {formatCellValue(meta, value)}
                        </span>
                      </div>
                    );
                  })}
                {showVariantSummary && (rec.portalData?.product || []).length > 0 && (
                  <table className="mt-1 w-full border-t border-zinc-200 text-[11px]" data-testid={`variant-summary-${rec.recordId}`}>
                    <thead>
                      <tr className="text-[10px] uppercase tracking-[0.03em] text-zinc-400">
                        <th className="pt-1 text-left font-normal">Size</th>
                        <th className="pt-1 text-right font-normal">QTY</th>
                        <th className="pt-1 text-right font-normal">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rec.portalData.product.map((v) => (
                        <tr key={v.recordId} className="border-t border-zinc-100 text-zinc-700">
                          <td className="py-0.5">{(v["product::Size"] || "").trim() || "-"}</td>
                          <td className="py-0.5 text-right font-mono-data">
                            {v["product::inventory qty"] != null ? Math.round(v["product::inventory qty"]) : "-"}
                          </td>
                          <td className="py-0.5 text-right font-mono-data">
                            ${(Number(v["product::Price"]) || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
